import type { SubsidiaryOption } from '@/@auth/types';
import { portalApi } from '@/lib/http/axios';

interface OrganizationListItem {
    orgId: number;
    orgName: string;
    sfAccountId?: string;
    isLicenseValid?: boolean;
    licenseEndDate?: string | Date | null;
}

interface OrgDropdownRow {
    /** Salesforce account ID */
    id: string;
    name: string;
    orgId: number;
}

export interface SubsidiaryScopeResponse {
    parentOrgId: number | null;
    role: 'parent' | 'subsidiary' | 'standalone';
    orgIds: number[];
    sfAccountIds: string[];
    hasSubsidiaryAccess: boolean;
    crossSubsidiaryWriteEnabled: boolean;
    options: SubsidiaryOption[];
    /** Server verdict for the Team → Subsidiary data access panel. */
    canManageSubsidiaryAccess?: boolean;
    manageableParentOrgId?: number | null;
}

/**
 * Full client list for internal View-as-org.
 * Uses the lightweight GET /organization/dropdown (includes orgId) — not
 * /organization/list, which Stripe/license-enriches every row and is too slow
 * for a global picker.
 */
export async function fetchInternalClientOptions(): Promise<SubsidiaryOption[]> {
    const { data: response } = await portalApi.get<OrgDropdownRow[] | { data: OrgDropdownRow[] }>(
        '/organization/dropdown',
    );

    // RestResponseInterceptor wraps as { data: [...] }
    const rows = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

    return rows
        .filter((org) => org.orgId && org.id)
        .map((org) => ({
            orgId: Number(org.orgId),
            sfAccountId: org.id,
            orgName: org.name,
        }));
}

/**
 * Resolve parent–subsidiary scope for an arbitrary org (internal View-as only).
 */
export async function fetchSubsidiaryScopeForOrg(
    forOrgId: number,
): Promise<SubsidiaryScopeResponse> {
    const { data } = await portalApi.get<
        SubsidiaryScopeResponse | { data: SubsidiaryScopeResponse }
    >('/organization/subsidiaries', { params: { forOrgId } });
    const payload = (data as { data?: SubsidiaryScopeResponse })?.data ?? data;
    return payload as SubsidiaryScopeResponse;
}

/**
 * Caller's own scope, including whether the server will let them manage the
 * group's subsidiary data access. Preferred over re-deriving that verdict from
 * session fields, which can be stale or resolved from the wrong org membership.
 */
export async function fetchSelfSubsidiaryScope(): Promise<SubsidiaryScopeResponse> {
    const { data } = await portalApi.get<
        SubsidiaryScopeResponse | { data: SubsidiaryScopeResponse }
    >('/organization/subsidiaries');
    const payload = (data as { data?: SubsidiaryScopeResponse })?.data ?? data;
    return payload as SubsidiaryScopeResponse;
}

export type { OrganizationListItem };
